import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import {
    WeezHeader,
    Card,
    Icon,
    Table,
    InfoText,
    Message,
    ContentCard,
    Filter,
    Modal,
    Button,
    FilterSetController,
    Status,
    Paginator,
    Tooltip,
    ToastContext,
    SelectButton,
    DropdownItem
} from '@weezevent/nacre';
import SubscriptionTicket from '../../../models/subscriptionTicket';
import Subscription from '../../../models/subscription';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import useAppContext from '../../../context/useAppContext';
import LoadingTable from '../../../components/loadingTable';
import Styles from './ticketList.module.css';
import SubscriptionLink from '../../../models/subscriptionLink';
import { handleSearchQuery } from '../../manager/components/context';
import { useCurrentPageFilters } from '../../../utils/filters';
import { SyncButton } from '../../../components/sync-button/sync-button';
import { TicketInfoDrawer } from './ticketInfoDrawer/ticketInfoDrawer';
import ResendTicketModal from '../resendTicketModal';

const spec = {
    multiple: ['status__in', 'validity__in', 'subscription_link_id__in'],
    fields: ['search', 'status__in', 'validity__in', 'subscription_link_id__in'],
    aliases: {},
    date: []
};

function TicketStatus({ ticket }) {
    const { t } = useTranslation();

    const status = React.useMemo(() => {
        if (ticket.status === 'done') {
            return {
                color: 'green',
                text: t('ticket.webservice.list.status-sent'),
                tooltip: t('ticket.webservice.list.status-sent-tooltip')
            };
        }
        if (ticket.status === 'unfiltered') {
            return {
                color: 'red',
                text: t('ticket.webservice.list.status-unfiltered'),
                tooltip: t('ticket.webservice.list.status-unfiltered-tooltip')
            };
        }
        if (ticket.status === 'pending' && !ticket.last_pushed_to_weez) {
            return {
                color: 'orange',
                text: t('ticket.webservice.list.status-pending'),
                tooltip: t('ticket.webservice.list.status-pending-tooltip')
            };
        }
        if (ticket.status === 'pending' && ticket.last_pushed_to_weez) {
            return {
                color: 'violet',
                text: t('ticket.webservice.list.status-updating'),
                tooltip: t('ticket.webservice.list.status-updating-tooltip')
            };
        }
    }, [ticket]);

    return (
        <Tooltip triggerOnHover title={status.tooltip}>
            <Status className={Styles['subscription-status']} {...status} />
        </Tooltip>
    );
}

function TicketValidity({ ticket }) {
    const { t } = useTranslation();

    const validity = React.useMemo(() => {
        if (ticket.removed_at) {
            return {
                color: 'red',
                text: t('ticket.webservice.ticket-list.filter.validity.options.invalid.label'),
                tooltip: t('ticket.webservice.ticket-list.filter.validity.options.invalid.tooltip')
            };
        }
        return {
            color: 'green',
            text: t('ticket.webservice.ticket-list.filter.validity.options.valid.label'),
            tooltip: null,
            className: Styles['validity_badge__without_tooltip']
        };
    }, [ticket]);

    if (!validity.tooltip) {
        return <Status {...validity} />;
    }

    return (
        <Tooltip title={validity.tooltip} triggerOnHover={true}>
            <Status {...validity} />
        </Tooltip>
    );
}

function HeaderRow({ allTicketsSelected }) {
    const { t } = useTranslation();

    return (
        <Table.Tr isHeader active={allTicketsSelected}>
            <th>{t('ticket.webservice.ticket-list.header.created')}</th>
            <th>{t('ticket.webservice.ticket-list.header.barcode')}</th>
            <th>{t('ticket.webservice.ticket-list.header.status')}</th>
            <th>{t('ticket.webservice.ticket-list.header.validity')}</th>
            <th className={Styles['width-x-small']}>{t('ticket.webservice.ticket-list.header.ticket-data')}</th>
        </Table.Tr>
    );
}

function TableRow({ ticket, timezone, onSelectSubscriptionTicket, active, onSelected }) {
    const locale = i18n.language;

    const date = React.useMemo(() => {
        const formated_date = new Date(ticket.created_at).toLocaleString(locale, { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' });
        const formated_hour = new Date(ticket.created_at).toLocaleString(locale, { timeZone: timezone, hour: '2-digit', minute: '2-digit' });
        return <InfoText subtitle={formated_hour}>{formated_date}</InfoText>;
    }, [ticket, timezone]);

    return (
        <Table.Tr active={active} onSelected={(event, data) => onSelected(event, data, ticket)}>
            <Table.Td>{date}</Table.Td>
            <Table.Td>
                <InfoText subtitle={ticket.barcode_id}>{ticket.ticket_identifier}</InfoText>
            </Table.Td>
            <Table.Td>
                <TicketStatus ticket={ticket} />
            </Table.Td>
            <Table.Td>
                <TicketValidity ticket={ticket} />
            </Table.Td>
            <Table.Td className={Styles['td-centered']}>
                <Icon
                    name="eye"
                    onClick={e => {
                        e.stopPropagation();
                        onSelectSubscriptionTicket(ticket);
                    }}
                />
            </Table.Td>
        </Table.Tr>
    );
}

TableRow.propTypes = {
    ticket: PropTypes.object,
    timezone: PropTypes.string.isRequired,
    onSelectSubscriptionTicket: PropTypes.func.isRequired,
    active: PropTypes.bool,
    onSelected: PropTypes.func.isRequired
};

function createQueryParams(filters, pagination) {
    const params = {
        limit: pagination.pageSize,
        offset: pagination.currentPage * pagination.pageSize,
        ...handleSearchQuery(spec, filters, true)
    };

    if (!params.validity__in) {
        // By default, we want to fetch both valid and invalid tickets
        // The backend defaults to only valid tickets if no filter is provided for validity__in
        params.validity__in = ['valid', 'invalid'];
    }

    return params;
}

const DEFAULT_FILTERS = {
    validity__in: new Set(['valid'])
};

export default function TicketList({ organizationId, subscriptionId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const [{ organization }] = useAppContext();
    const [selectedSubscriptionTicket, setSelectedSubscriptionTicket] = React.useState(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [filters, setFilters] = useCurrentPageFilters(spec);
    const toast = React.useContext(ToastContext);

    const [selectedTicketIds, setSelectedTicketIds] = React.useState([]);
    const [openModalResendTicket, setOpenModalResendTicket] = React.useState(false);
    const [openModalResyncTicket, setOpenModalResyncTicket] = React.useState(false);

    const [currentPage, setCurrentPage] = React.useState(0);
    const [pageSize] = React.useState(100);

    const queryParams = React.useMemo(() => {
        return createQueryParams(filters, { currentPage, pageSize });
    }, [
        // We have to list all filter keys here to avoid unnecessary re-fetches
        filters.search,
        filters.status__in,
        filters.validity__in,
        filters.subscription_link_id__in,
        currentPage,
        pageSize
    ]);

    const [subscriptionTickets, loadingSubscriptionTicket] = SubscriptionTicket.useApiModel(
        {
            cache: false,
            allow_cache: false,
            organizationId: organizationId,
            subscriptionId: subscriptionId,
            query: queryParams
        },
        [organizationId, subscriptionId, queryParams]
    );

    const [subscription, loadingSubscription] = Subscription.useApiModel(
        {
            cache: false,
            allow_cache: false,
            organizationId: organizationId,
            id: subscriptionId
        },
        [organizationId, subscriptionId]
    );

    const [subscriptionLink] = SubscriptionLink.useApiModel(
        {
            cache: false,
            allow_cache: false,
            organizationId,
            subscriptionId
        },
        [organizationId, subscriptionId]
    );

    React.useEffect(() => {
        // Set default validity filter to 'valid' if not set
        if (!filters.validity__in) {
            setFilters({ ...filters, validity__in: new Set(['valid']) });
        }
    }, []);

    const managementRuleOptions = React.useMemo(() => {
        if (!subscriptionLink) return [];
        return subscriptionLink.map(link => link.toOptions());
    }, [subscriptionLink]);

    const onCloseModal = React.useCallback(() => {
        setSelectedSubscriptionTicket(null);
    }, []);

    const totalCount = React.useMemo(() => {
        return Number.parseInt(subscriptionTickets?.__meta?.headers.get('total-count')) || 0;
    }, [subscriptionTickets]);

    const handlePage = React.useCallback(value => {
        setCurrentPage(value);
    }, []);

    const filteredTickets = React.useMemo(() => {
        if (!subscriptionTickets?.length) {
            return [];
        }
        return subscriptionTickets;
    }, [subscriptionTickets]);

    const options = [
        { key: 'unfiltered', text: t('ticket.webservice.list.status-unfiltered'), name: 'unfiltered' },
        { key: 'pending', text: t('ticket.webservice.list.status-pending'), name: 'pending' },
        { key: 'done', text: t('ticket.webservice.list.status-sent'), name: 'done' },
        { key: 'updating', text: t('ticket.webservice.list.status-updating'), name: 'updating' }
    ];

    const handleSearch = React.useCallback(
        value => {
            setFilters({ ...filters, search: value });
            setCurrentPage(0);
        },
        [filters, setFilters]
    );

    const resyncTicket = React.useCallback(() => {
        setIsSubmitting(true);
        Subscription.push_tickets({ organizationId, subscriptionId: subscription.pk })
            .then(() => {
                toast.success(t('ticket.webservice.management-rules.toast_sync_success'));
                setOpenModalResyncTicket(false);
            })
            .catch(() => {
                toast.error(t('ticket.common.toast_error'));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [subscription, organizationId, toast]);

    const onTicketSelected = React.useCallback((event, { checked }, ticket) => {
        event.stopPropagation();
        setSelectedTicketIds(currentIds => {
            if (checked) {
                return [...currentIds, ticket.id];
            }
            return currentIds.filter(id => id !== ticket.id);
        });
    }, []);

    const handleSelectAll = React.useCallback(
        ({ event, data: { checked } }) => {
            if (!checked) {
                setSelectedTicketIds([]);
                return;
            }
            const allIds = filteredTickets.map(ticket => ticket.id);
            setSelectedTicketIds(allIds);
        },
        [filteredTickets]
    );

    return (
        <div>
            <Modal
                title={t('ticket.webservice.management-rules.modal-sync-sub.title')}
                onClose={() => setOpenModalResyncTicket(false)}
                open={openModalResyncTicket}
                submitButton={<Button primary onClick={() => resyncTicket()} label={t('ticket.common.action.confirm')} disabled={isSubmitting} />}
                cancelButton={<Button onClick={() => setOpenModalResyncTicket(false)} label={t(`ticket.common.action.cancel`)} />}
            >
                <Message title={t('ticket.webservice.management-rules.modal-sync-sub.warning-card.title')} type="danger">
                    <p>{t('ticket.webservice.management-rules.modal-sync-sub.warning-card.text')}</p>
                </Message>
            </Modal>

            {openModalResendTicket && (
                <ResendTicketModal
                    show={openModalResendTicket}
                    onClose={() => {
                        setOpenModalResendTicket(false);
                        setSelectedTicketIds([]);
                    }}
                    organizationId={organizationId}
                    subscriptionId={subscriptionId}
                    ticketIds={selectedTicketIds}
                />
            )}

            <WeezHeader
                title={subscription?.name || subscription?.type}
                // we need backlink only if subscription is not a hook, because in this case we have the webservice context menu to go back
                backLink={subscription?.type !== 'hook' ? { onClick: () => history.push(`/ticket/O${organizationId}/webservices/${subscriptionId}`) } : undefined}
                rightComponent={[
                    <Tooltip key="sync-tooltip" triggerOnHover position={'bottom'} title={t('ticket.webservice.management-rules.sync-tooltip')}>
                        <SyncButton sync={() => setOpenModalResyncTicket(true)} />
                    </Tooltip>
                ]}
            />
            {!subscriptionLink?.length && subscriptionTickets?.length > 0 ? (
                <Message
                    title={t('ticket.webservice.ticket-list.message.title')}
                    type="danger"
                    links={[
                        {
                            label: t('ticket.webservice.ticket-list.message.link'),
                            href: `/ticket/O${organizationId}/webservices/${subscriptionId}/mapping`
                        },
                        {
                            label: t('ticket.webservice.ticket-list.message.rule-link'),
                            href: `/ticket/O${organizationId}/webservices/${subscriptionId}/subscription_links`
                        }
                    ]}
                >
                    {t('ticket.webservice.ticket-list.message.body')}
                </Message>
            ) : !loadingSubscriptionTicket && !subscriptionTickets?.length && !subscriptionLink?.length ? (
                <Message title={t('ticket.manager.attendees.noAttendees')} type="danger">
                    {t('ticket.webservice.ticket-list.empty-card-body')}
                </Message>
            ) : null}
            <TicketInfoDrawer ticket={selectedSubscriptionTicket} openSideBar={Boolean(selectedSubscriptionTicket)} onCloseSideBar={onCloseModal} />
            <Card>
                <ContentCard>
                    <FilterSetController
                        total={totalCount}
                        isLoadingTotal={loadingSubscriptionTicket}
                        labelTotal={t('ticket.common.attendee', { count: totalCount })}
                        labelReset={t('ticket.common.action.reset')}
                        onChange={values => {
                            if (filters.search !== undefined && filters.search.length > 0) {
                                values.search = filters.search;
                            } else {
                                delete values.search;
                            }
                            setFilters(values);
                            setCurrentPage(0);
                        }}
                        initialValues={{ ...DEFAULT_FILTERS, ...filters }}
                        defaultValues={DEFAULT_FILTERS}
                        onReset={() => {
                            setFilters(DEFAULT_FILTERS);
                            setCurrentPage(0);
                        }}
                        search={{
                            onChange: (_, search = '') => handleSearch(search),
                            handleReset: () => {
                                setFilters(filters => ({ ...filters, search: '' }));
                                setCurrentPage(0);
                            },
                            placeholder: t('ticket.common.action.search'),
                            value: filters?.search || ''
                        }}
                    >
                        <Filter
                            name="status__in"
                            labelSelectAll={t('ticket.common.action.select-all')}
                            labelName={t('ticket.accounting.filter.statusLabel')}
                            multiple
                            options={options}
                            index="1"
                        />
                        <Filter
                            name="validity__in"
                            labelSelectAll={t('ticket.common.action.select-all')}
                            labelName={t('ticket.webservice.ticket-list.filter.validity.label')}
                            multiple
                            options={[
                                { key: 'valid', text: t('ticket.webservice.ticket-list.filter.validity.options.valid.label'), name: 'valid' },
                                { key: 'invalid', text: t('ticket.webservice.ticket-list.filter.validity.options.invalid.label'), name: 'invalid' }
                            ]}
                            index="2"
                        />
                        <Filter
                            name="subscription_link_id__in"
                            labelSelectAll={t('ticket.common.action.select-all')}
                            labelName={t('ticket.webservice.ticket-list.filter.management_rule')}
                            options={managementRuleOptions}
                            multiple
                            index="3"
                        />
                    </FilterSetController>
                    <div className={Styles['actions-container']}>
                        <SelectButton
                            tooltipTitle={t('ticket.webservice.ticket-list.actions.no-action-tooltip')}
                            title={
                                selectedTicketIds.length > 0
                                    ? t(
                                          selectedTicketIds.length > 1
                                              ? 'ticket.webservice.ticket-list.selection.selected-count-plural'
                                              : 'ticket.webservice.ticket-list.selection.selected-count',
                                          { count: selectedTicketIds.length }
                                      )
                                    : t('ticket.common.action.bulk')
                            }
                            objects={filteredTickets.filter(ticket => selectedTicketIds.includes(ticket.id))}
                        >
                            <DropdownItem item={t('ticket.webservice.ticket-list.actions.resend')} onClick={() => setOpenModalResendTicket(true)} />
                        </SelectButton>
                    </div>
                </ContentCard>
                <Table useNew selectable onSelectAll={(event, data) => handleSelectAll({ event, data })} className={Styles['attendees_table']}>
                    <Table.THead>
                        <HeaderRow allTicketsSelected={Boolean(filteredTickets.length > 0 && selectedTicketIds.length === filteredTickets.length)} />
                    </Table.THead>
                    <Table.TBody>
                        {filteredTickets.map((ticket, index) => (
                            <TableRow
                                key={index}
                                ticket={ticket}
                                timezone={organization.timezone}
                                onSelectSubscriptionTicket={setSelectedSubscriptionTicket}
                                active={selectedTicketIds.includes(ticket.id)}
                                onSelected={onTicketSelected}
                            />
                        ))}
                    </Table.TBody>
                </Table>
                <LoadingTable loading={loadingSubscription || loadingSubscriptionTicket} />
                {!loadingSubscriptionTicket && !loadingSubscription && !filteredTickets.length && (
                    <Card.EmptyState title={t('ticket.manager.attendees.noAttendees')} subtitle={t('ticket.manager.attendees.noAttendeesSubtitle')} icon={<Icon name="user" />} />
                )}
                <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
                    <Paginator currentPage={currentPage} onPageChange={handlePage} pageSize={pageSize} totalCount={totalCount} />
                </div>
            </Card>
        </div>
    );
}
TicketList.propTypes = {
    organizationId: PropTypes.string.isRequired,
    subscriptionId: PropTypes.string.isRequired
};
